import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import adminService from '../../utils/adminService';
import SEO from '../../components/SEO';
import OnboardingWelcome from './components/OnboardingWelcome';
import OnboardingProfile from './components/OnboardingProfile';
import OnboardingBilling from './components/OnboardingBilling';
import OnboardingVerification from './components/OnboardingVerification';
import OnboardingComplete from './components/OnboardingComplete';
import { CheckCircle, Circle } from 'lucide-react';

const ClientOnboarding = () => {
  const { user, userProfile } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [onboardingData, setOnboardingData] = useState({
    stage: 'welcome',
    completed_steps: [],
    client_data: {},
  });
  const [loading, setLoading] = useState(false);

  const steps = [
    { id: 'welcome', label: 'Welcome', component: OnboardingWelcome },
    { id: 'profile', label: 'Profile Setup', component: OnboardingProfile },
    { id: 'billing', label: 'Billing Information', component: OnboardingBilling },
    { id: 'verification', label: 'Verification', component: OnboardingVerification },
    { id: 'completed', label: 'Complete', component: OnboardingComplete },
  ];

  useEffect(() => {
    if (user && userProfile) {
      loadOnboardingData();
    }
  }, [user, userProfile]);

  const loadOnboardingData = async () => {
    try {
      // Get current onboarding status
      const result = await adminService.getOnboardingStatus();
      
      if (result.success) {
        const userOnboarding = result.data?.find(item => 
          item.clients?.user_id === user.id
        );
        
        if (userOnboarding) {
          setOnboardingData(userOnboarding);
          const currentStepIndex = steps.findIndex(step => step.id === userOnboarding.stage);
          setCurrentStep(currentStepIndex >= 0 ? currentStepIndex : 0);
        }
      }
    } catch (error) {
      console.log('Error loading onboarding data:', error);
    }
  };

  const handleNextStep = async (stepData = {}) => {
    try {
      setLoading(true);
      
      // Update onboarding data
      const updatedData = {
        ...onboardingData,
        client_data: {
          ...onboardingData.client_data,
          ...stepData,
        },
      };

      const nextStepIndex = currentStep + 1;
      const nextStep = steps[nextStepIndex];
      
      if (nextStep) {
        // Update completed steps
        const completedSteps = [...(onboardingData.completed_steps || [])];
        if (!completedSteps.includes(steps[currentStep].id)) {
          completedSteps.push(steps[currentStep].id);
        }

        // Update onboarding stage
        const result = await adminService.updateOnboardingStage(
          onboardingData.client_id,
          nextStep.id,
          completedSteps
        );

        if (result.success) {
          setOnboardingData({
            ...updatedData,
            stage: nextStep.id,
            completed_steps: completedSteps,
          });
          setCurrentStep(nextStepIndex);
        }
      }
    } catch (error) {
      console.log('Error updating onboarding:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const CurrentStepComponent = steps[currentStep]?.component;

  if (!user || !userProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading onboarding...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Client Onboarding"
        description="Complete your EmailValidator client onboarding process. Set up your profile, billing information, and verification to start using our email validation services."
        keywords="client onboarding, account setup, profile setup, billing setup, verification process, onboarding flow"
        canonical="https://mailvalidator.octalchip.com/client-onboarding"
      />
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`flex items-center ${
                  index < steps.length - 1 ? 'flex-1' : ''
                }`}
              >
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      index <= currentStep
                        ? 'bg-blue-600 text-white' :'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {index < currentStep || onboardingData.completed_steps?.includes(step.id) ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <Circle className="w-5 h-5" />
                    )}
                  </div>
                  <span
                    className={`text-xs mt-2 ${
                      index <= currentStep ? 'text-blue-600' : 'text-gray-500'
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-4 ${
                      index < currentStep ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Current Step Content */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {CurrentStepComponent && (
            <CurrentStepComponent
              data={onboardingData.client_data}
              userProfile={userProfile}
              onNext={handleNextStep}
              onPrev={handlePrevStep}
              loading={loading}
              isFirstStep={currentStep === 0}
              isLastStep={currentStep === steps.length - 1}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientOnboarding;